/**
 * Boolean proposal card (the runtime HITL gate for `boolean_operation`). Pure
 * presentational: it shows the EXPLICIT ORDERED steps as a NUMBERED list (so a
 * `#k` operand is unambiguous — the displayed step numbers are the reference
 * targets), each step's operands combined LEFT-TO-RIGHT by its one operation
 * (no hidden operator precedence — ui_specs "show steps"), the new structure
 * name, and the previewed composite volume + delta; nothing mutates until the
 * clinician presses Apply. The card frame, status chip, hover preview, and
 * Apply/Reject gate live in {@link ProposalCardShell}; this component supplies
 * only the body.
 */
import { alpha, Box, Typography } from '@mui/material';
import { isStepReference, type BooleanProposalStep } from '~/agent';
import { formatCm3, formatSignedCm3 } from '~/utils/units';
import ProposalCardShell, {
    MONOSPACE_FONT_FAMILY,
    PreviewedVolumeLine,
    type ProposalStatus,
} from './ProposalCardShell';

/**
 * The ordering note only helps when ordering is actually load-bearing: more
 * than one step, a step that chains more than two operands, or a `#k`
 * back-reference. A single two-operand step (e.g. `Body subtract Ring`) reads
 * for itself, so the note is omitted rather than showing empty jargon.
 */
function orderingNote(
    steps: ReadonlyArray<BooleanProposalStep>
): string | null {
    const hasStepRef = steps.some((s) => s.operands.some(isStepReference));
    const multiOp =
        steps.length > 1 || steps.some((s) => s.operands.length > 2);
    if (!multiOp && !hasStepRef) {
        return null;
    }
    const base = 'Runs top to bottom.';
    return hasStepRef ? `${base} “#k” reuses the result of step k.` : base;
}

export interface BooleanProposalCardProps {
    readonly steps: ReadonlyArray<BooleanProposalStep>;
    readonly newRoi: string;
    readonly compositeVolumeCm3: number;
    readonly volumeDeltaCm3: number;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

export default function BooleanProposalCard({
    steps,
    newRoi,
    compositeVolumeCm3,
    volumeDeltaCm3,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: BooleanProposalCardProps) {
    const note = orderingNote(steps);
    return (
        <ProposalCardShell
            title="Boolean operation"
            ariaLabel="Boolean operation proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            {note !== null && (
                <Typography variant="caption" color="text.secondary">
                    {note}
                </Typography>
            )}

            {/* Ordered list: the 1-based item number IS the `#k` reference
                target — the evaluation order is explicit. */}
            <Box
                component="ol"
                sx={(t) => ({
                    m: 0,
                    pl: 3,
                    fontFamily: MONOSPACE_FONT_FAMILY,
                    fontSize: '0.78rem',
                    color: t.palette.primary.main,
                    backgroundColor: alpha(t.palette.common.black, 0.06),
                    py: 0.75,
                    pr: 1,
                    borderRadius: 0.5,
                    '& li': { mb: 0.25 },
                    '& li:last-of-type': { mb: 0 },
                })}
            >
                {steps.map((step, i) => (
                    <li key={i}>
                        {step.operands.map((operand, j) => (
                            <span key={j}>
                                {j > 0 && (
                                    <Box
                                        component="span"
                                        sx={{ fontWeight: 700 }}
                                    >
                                        {' '}
                                        {step.op}{' '}
                                    </Box>
                                )}
                                {operand}
                            </span>
                        ))}
                    </li>
                ))}
            </Box>

            <Box
                sx={(t) => ({
                    fontFamily: MONOSPACE_FONT_FAMILY,
                    fontSize: '0.78rem',
                    color: t.palette.primary.main,
                })}
            >
                {'→ '}
                <strong>{newRoi}</strong>
            </Box>

            <PreviewedVolumeLine
                label="Previewed composite volume"
                value={`${formatCm3(compositeVolumeCm3)} (${formatSignedCm3(
                    volumeDeltaCm3
                )} vs first operand)`}
            />
        </ProposalCardShell>
    );
}
